/**
 * خدمات المرفقات: رفع متحقَّق منه، روابط موقّعة مؤقتة، حذف فعلي، وسياسة احتفاظ.
 * لا تُنشأ روابط عامة دائمة، ولا يُحذف سجل المهمة عند حذف صورته.
 */

import type { DataGateway, ApplyRetentionInput } from '@/data/types';
import type { Attachment, AttachmentDeletion, AttachmentKind } from '@/domain/types';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import { DEFAULT_UPLOAD_POLICY, type UploadPolicy } from '@/domain/settings';
import type { Actor } from './org-service';

export interface UploadFileInput {
  companyId: string;
  taskId: string | null;
  taskAssigneeId?: string | null;
  file: File;
  kind: AttachmentKind;
  clientRef?: string;
  policy?: UploadPolicy;
}

export function validateUpload(file: Pick<File, 'name' | 'type' | 'size'>, policy: UploadPolicy): void {
  if (file.size <= 0) throw AppError.validation('الملف فارغ');
  if (file.size > policy.maxSizeMb * 1024 * 1024) {
    throw AppError.validation(`حجم الملف يتجاوز الحد (${policy.maxSizeMb} ميجابايت)`);
  }
  if (!policy.allowedMimeTypes.includes(file.type)) {
    throw AppError.validation(
      `نوع الملف غير مسموح (${file.type || 'غير معروف'}). الأنواع المسموحة: صور ومستندات PDF/Word/Excel.`,
    );
  }
}


/**
 * ضغط بصري فائق الكفاءة للصور العادية مع الحفاظ التام على الجودة والحدة.
 * يحول الصور إلى صيغة WebP مع تخفيض الحجم بما يصل إلى 80% دون أي خسارة بصرية.
 */
async function compressImageIfBeneficial(file: File): Promise<File> {
  if (typeof document === 'undefined' || !file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return file;
  }
  // إذا كان حجم الصورة صغيراً جداً بالفعل (أقل من 150 كيلوبايت) لا داعي لمعالجتها
  if (file.size < 150 * 1024) {
    return file;
  }

  return new Promise<File>((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        URL.revokeObjectURL(objectUrl);
        const maxDim = 2048; // دقة 2K عالية الوضوح
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) {
          resolve(file);
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const baseName = file.name.replace(/\.[^.]+$/, '');
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size > 0 && blob.size < file.size) {
              const compressed = new File([blob], `${baseName}.webp`, {
                type: 'image/webp',
                lastModified: Date.now(),
              });
              resolve(compressed);
            } else {
              resolve(file);
            }
          },
          'image/webp',
          0.88,
        );
      } catch {
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });
}

export async function uploadAttachment(
  gateway: DataGateway,
  actor: Actor,
  input: UploadFileInput,
): Promise<Attachment> {
  const policy = input.policy ?? DEFAULT_UPLOAD_POLICY;
  const processedFile = await compressImageIfBeneficial(input.file);
  validateUpload(processedFile, policy);
  const bytes = await processedFile.arrayBuffer();
  return gateway.commands.uploadAttachment({
    companyId: input.companyId,
    taskId: input.taskId,
    taskAssigneeId: input.taskAssigneeId ?? null,
    fileName: processedFile.name,
    mimeType: processedFile.type,
    kind: input.kind,
    bytes,
    uploadedByUserId: actor.userId,
    clientRef: input.clientRef ?? newId(),
  });
}

/** صور الهوية التنظيمية تستخدم الأنواع المدعومة من bucket الخاص فقط. */
export async function uploadOrganizationLogo(
  gateway: DataGateway,
  actor: Actor,
  companyId: string,
  file: File,
): Promise<Attachment> {
  return uploadAttachment(gateway, actor, {
    companyId,
    taskId: null,
    file,
    kind: 'logo',
    policy: {
      maxSizeMb: 10,
      allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    },
  });
}

export async function listTaskAttachments(gateway: DataGateway, taskId: string): Promise<Attachment[]> {
  const rows = await gateway.table('attachments').list({
    filter: { taskId: { op: 'eq', value: taskId }, deletedAt: { op: 'isNull' } },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });
  return rows.filter((row) => row.uploadState !== 'deleted' && !row.storagePath.startsWith('tf-meta://'));
}

export async function attachmentUrl(
  gateway: DataGateway,
  attachmentId: string,
  expiresInSeconds = 300,
): Promise<string> {
  return gateway.commands.createAttachmentUrl({ attachmentId, expiresInSeconds });
}

export async function deleteAttachment(
  gateway: DataGateway,
  actor: Actor,
  attachmentId: string,
): Promise<void> {
  await gateway.commands.deleteAttachment({
    attachmentId,
    actorUserId: actor.userId,
    mode: 'manual',
  });
}

export async function previewRetention(
  gateway: DataGateway,
  input: Omit<ApplyRetentionInput, 'dryRun'>,
): Promise<{ filesCount: number; bytesFreed: number }> {
  const result = await gateway.commands.applyRetention({ ...input, dryRun: true });
  return { filesCount: result.filesCount, bytesFreed: result.bytesFreed };
}

export async function applyRetention(
  gateway: DataGateway,
  input: Omit<ApplyRetentionInput, 'dryRun'>,
): Promise<{ filesCount: number; bytesFreed: number; deletionId: string | null }> {
  return gateway.commands.applyRetention({ ...input, dryRun: false });
}

export async function listDeletions(
  gateway: DataGateway,
  companyId: string,
): Promise<AttachmentDeletion[]> {
  return gateway.table('attachment_deletions').list({
    filter: { companyId: { op: 'eq', value: companyId } },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
    limit: 100,
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} بايت`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} كيلوبايت`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} ميجابايت`;
}
